import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { toast } from "sonner";
import { Button } from "../../../Components/ui/buttons/button";
import { Check, Eye, Send, X } from "../../../Components/ui/icons";
import Typography from "../../../Components/Typography";
import {
  AdminRegistrationRequestResponse,
  AdminRegistrationRequestStatusFilter,
  RegistrationRequestStatus,
} from "../../../global/types/registrationRequest";
import {
  approveAdminRegistrationRequest,
  getAdminRegistrationRequest,
  listAdminRegistrationRequests,
  rejectAdminRegistrationRequest,
  resendAdminRegistrationRequestEmail,
} from "../../../services/registrationRequest";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { TabPanelProps } from "./UserProfileTabs";

const STATUS_FILTERS: Array<{
  value: AdminRegistrationRequestStatusFilter;
  label: string;
}> = [
  { value: "PENDING", label: "Pendentes" },
  { value: "APPROVED", label: "Aprovadas" },
  { value: "REJECTED", label: "Rejeitadas" },
  { value: "ALL", label: "Todas" },
];

const STATUS_LABELS: Record<RegistrationRequestStatus, string> = {
  PENDING: "Pendente",
  APPROVED: "Aprovada",
  REJECTED: "Rejeitada",
};

const STATUS_COLORS: Record<
  RegistrationRequestStatus,
  "warning" | "success" | "error"
> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const DEFAULT_ROWS_PER_PAGE = 25;
const MAX_REJECTION_REASON_LENGTH = 2000;

const formatIsoDate = (value?: string | null) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14) return value;

  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

export const getEmailResendBlockReason = (
  request: AdminRegistrationRequestResponse
) => {
  if (request.status !== "APPROVED") {
    return "Reenvio disponível apenas para solicitações aprovadas.";
  }

  if (!request.email_error) {
    return "Sem erro de envio registrado.";
  }

  if (request.email_sent_at) {
    return "E-mail já enviado.";
  }

  return null;
};

interface DetailFieldProps {
  label: string;
  value?: React.ReactNode;
}

function DetailField({ label, value }: DetailFieldProps) {
  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <Box>
      <Typography variant="miniText" className="text-[#676A70]">
        {label}
      </Typography>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography variant="body4">{hasValue ? value : "N/A"}</Typography>
      )}
    </Box>
  );
}

interface StatusChipProps {
  status: RegistrationRequestStatus;
}

function StatusChip({ status }: StatusChipProps) {
  return (
    <Chip
      color={STATUS_COLORS[status]}
      label={STATUS_LABELS[status]}
      size="small"
      variant="outlined"
    />
  );
}

export default function RegistrationRequestsTab(props: TabPanelProps) {
  const { value, index } = props;
  const [requests, setRequests] = useState<AdminRegistrationRequestResponse[]>(
    []
  );
  const [statusFilter, setStatusFilter] =
    useState<AdminRegistrationRequestStatusFilter>("PENDING");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveCandidate, setApproveCandidate] =
    useState<AdminRegistrationRequestResponse | null>(null);
  const [rejectCandidate, setRejectCandidate] =
    useState<AdminRegistrationRequestResponse | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [detailRequest, setDetailRequest] =
    useState<AdminRegistrationRequestResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const latestFetchIdRef = useRef(0);

  const fetchRequests = useCallback(async () => {
    const fetchId = latestFetchIdRef.current + 1;
    latestFetchIdRef.current = fetchId;

    setLoading(true);
    setError(null);

    try {
      const status = statusFilter === "ALL" ? undefined : statusFilter;
      const response = await listAdminRegistrationRequests({
        status,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      });

      // Ignora respostas fora de ordem: só a última chamada disparada aplica.
      if (fetchId !== latestFetchIdRef.current) return;

      setRequests(response.items);
      setTotal(response.total);
    } catch (requestError) {
      if (fetchId !== latestFetchIdRef.current) return;
      setError(getApiErrorMessage(requestError));
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    if (value === index) {
      fetchRequests();
    }
  }, [fetchRequests, index, value]);

  const pendingCountLabel = useMemo(() => {
    if (statusFilter !== "PENDING") return null;
    if (total === 1) return "1 solicitação pendente";
    return `${total} solicitações pendentes`;
  }, [statusFilter, total]);

  const handleStatusFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextStatus: AdminRegistrationRequestStatusFilter | null
  ) => {
    if (!nextStatus) return;

    setStatusFilter(nextStatus);
    setPage(0);
  };

  const handlePageChange = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApprove = async () => {
    if (!approveCandidate) return;

    setActionLoading(true);
    setError(null);

    try {
      await approveAdminRegistrationRequest(approveCandidate.id);
      toast("Solicitação aprovada", {
        description: "A conta foi criada e o envio do e-mail será processado.",
      });
      setApproveCandidate(null);
      await fetchRequests();
    } catch (approveError) {
      const message = getApiErrorMessage(approveError);
      setError(message);
      toast("Erro ao aprovar solicitação", { description: message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectCandidate) return;

    const reason = rejectionReason.trim();
    if (!reason) return;

    setActionLoading(true);
    setError(null);

    try {
      await rejectAdminRegistrationRequest(rejectCandidate.id, reason);
      toast("Solicitação rejeitada", {
        description: "O motivo de rejeição foi registrado.",
      });
      setRejectCandidate(null);
      setRejectionReason("");
      await fetchRequests();
    } catch (rejectError) {
      const message = getApiErrorMessage(rejectError);
      setError(message);
      toast("Erro ao rejeitar solicitação", { description: message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenDetail = async (
    request: AdminRegistrationRequestResponse
  ) => {
    setDetailRequest(request);
    setDetailLoading(true);

    try {
      const detail = await getAdminRegistrationRequest(request.id);
      setDetailRequest(detail);
    } catch (detailError) {
      toast("Erro ao carregar detalhes", {
        description: getApiErrorMessage(detailError),
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!detailRequest) return;

    setActionLoading(true);
    setError(null);

    try {
      const updatedRequest = await resendAdminRegistrationRequestEmail(
        detailRequest.id
      );
      setDetailRequest(updatedRequest);
      setRequests((currentRequests) =>
        currentRequests.map((request) =>
          request.id === updatedRequest.id ? updatedRequest : request
        )
      );

      if (updatedRequest.email_error && !updatedRequest.email_sent_at) {
        toast("Tentativa concluída", {
          description: "O e-mail ainda não foi enviado. Verifique o erro.",
        });
      } else {
        toast("E-mail reenviado", {
          description: "A falha de envio foi resolvida.",
        });
      }
    } catch (resendError) {
      const message = getApiErrorMessage(resendError);
      setError(message);
      toast("Erro ao reenviar e-mail", { description: message });
    } finally {
      setActionLoading(false);
    }
  };

  const closeRejectDialog = () => {
    setRejectCandidate(null);
    setRejectionReason("");
  };

  const isRejectionReasonInvalid =
    rejectionReason.trim().length === 0 ||
    rejectionReason.length > MAX_REJECTION_REASON_LENGTH;

  const resendBlockReason = detailRequest
    ? getEmailResendBlockReason(detailRequest)
    : "Selecione uma solicitação.";

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4">Solicitações de cadastro</Typography>
            <Typography variant="body4" className="text-[#676A70] pt-2">
              Revise solicitações públicas, aprove contas e acompanhe falhas de
              envio de e-mail.
            </Typography>
          </Box>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <ToggleButtonGroup
              exclusive
              onChange={handleStatusFilterChange}
              size="small"
              value={statusFilter}
            >
              {STATUS_FILTERS.map((filter) => (
                <ToggleButton key={filter.value} value={filter.value}>
                  {filter.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {pendingCountLabel && (
              <Typography variant="body4" className="text-[#676A70]">
                {pendingCountLabel}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome completo</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Instituição</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Cidade</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 4 }}>
                      <Typography variant="body4">
                        Nenhuma solicitação encontrada para o filtro atual.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => {
                    const canReview = request.status === "PENDING";
                    const hasEmailFailure =
                      request.status === "APPROVED" &&
                      Boolean(request.email_error) &&
                      !request.email_sent_at;

                    return (
                      <TableRow hover key={request.id}>
                        <TableCell>{request.full_name}</TableCell>
                        <TableCell>{request.email}</TableCell>
                        <TableCell>{request.institution_name}</TableCell>
                        <TableCell>
                          {formatCnpj(request.institution_cnpj)}
                        </TableCell>
                        <TableCell>{request.institution_city}</TableCell>
                        <TableCell>
                          {formatIsoDate(request.created_at)}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            <StatusChip status={request.status} />
                            {hasEmailFailure && (
                              <Chip
                                color="error"
                                label="Falha no e-mail"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              onClick={() => handleOpenDetail(request)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              <Eye />
                              Detalhes
                            </Button>
                            {canReview && (
                              <>
                                <Button
                                  disabled={actionLoading}
                                  onClick={() => setApproveCandidate(request)}
                                  size="sm"
                                  type="button"
                                >
                                  <Check />
                                  Aprovar
                                </Button>
                                <Button
                                  disabled={actionLoading}
                                  onClick={() => setRejectCandidate(request)}
                                  size="sm"
                                  type="button"
                                  variant="destructive"
                                >
                                  <X />
                                  Rejeitar
                                </Button>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            labelRowsPerPage="Linhas por página:"
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />

          <Dialog
            onClose={() => setApproveCandidate(null)}
            open={Boolean(approveCandidate)}
          >
            <DialogTitle>Aprovar solicitação</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Aprovar solicitação de {approveCandidate?.full_name}? A conta
                será criada e o envio do e-mail será processado.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={actionLoading}
                onClick={() => setApproveCandidate(null)}
                type="button"
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                disabled={actionLoading}
                onClick={handleApprove}
                type="button"
              >
                <Check />
                Aprovar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            maxWidth="sm"
            onClose={closeRejectDialog}
            open={Boolean(rejectCandidate)}
          >
            <DialogTitle>Rejeitar solicitação</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Informe o motivo de rejeição para {rejectCandidate?.full_name}.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                helperText={`${rejectionReason.length}/${MAX_REJECTION_REASON_LENGTH} caracteres`}
                inputProps={{ maxLength: MAX_REJECTION_REASON_LENGTH }}
                label="Motivo de rejeição"
                minRows={4}
                multiline
                onChange={(event) => setRejectionReason(event.target.value)}
                value={rejectionReason}
              />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={actionLoading}
                onClick={closeRejectDialog}
                type="button"
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                disabled={actionLoading || isRejectionReasonInvalid}
                onClick={handleReject}
                type="button"
                variant="destructive"
              >
                <X />
                Rejeitar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            maxWidth="md"
            onClose={() => setDetailRequest(null)}
            open={Boolean(detailRequest)}
          >
            <DialogTitle>Detalhes da solicitação</DialogTitle>
            <DialogContent>
              {detailLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {detailRequest && (
                <Box sx={{ display: "grid", gap: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <DetailField
                      label="Nome completo"
                      value={detailRequest.full_name}
                    />
                    <DetailField label="E-mail" value={detailRequest.email} />
                    <DetailField
                      label="Instituição"
                      value={detailRequest.institution_name}
                    />
                    <DetailField
                      label="CNPJ"
                      value={formatCnpj(detailRequest.institution_cnpj)}
                    />
                    <DetailField
                      label="Cidade"
                      value={detailRequest.institution_city}
                    />
                    <DetailField
                      label="Data da solicitação"
                      value={formatIsoDate(detailRequest.created_at)}
                    />
                    <DetailField
                      label="Status"
                      value={<StatusChip status={detailRequest.status} />}
                    />
                    <DetailField
                      label="Atualizada em"
                      value={formatIsoDate(detailRequest.updated_at)}
                    />
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <DetailField
                      label="Revisor"
                      value={detailRequest.reviewed_by_name}
                    />
                    <DetailField
                      label="E-mail do revisor"
                      value={detailRequest.reviewed_by_email}
                    />
                    <DetailField
                      label="Data da revisão"
                      value={formatIsoDate(detailRequest.reviewed_at)}
                    />
                    <DetailField
                      label="Usuário criado no Keycloak"
                      value={detailRequest.created_keycloak_user_id}
                    />
                    <DetailField
                      label="E-mail enviado em"
                      value={formatIsoDate(detailRequest.email_sent_at)}
                    />
                  </Box>

                  {detailRequest.rejection_reason && (
                    <Alert severity="warning">
                      <Typography variant="body4">
                        {detailRequest.rejection_reason}
                      </Typography>
                    </Alert>
                  )}

                  {detailRequest.email_error && (
                    <Alert severity="error">
                      <Typography variant="miniText">
                        Erro de envio de e-mail
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 13,
                          mb: 0,
                          mt: 1,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {detailRequest.email_error}
                      </Box>
                    </Alert>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Tooltip title={resendBlockReason || "Reenviar e-mail"}>
                <span>
                  <Button
                    disabled={Boolean(resendBlockReason) || actionLoading}
                    onClick={handleResendEmail}
                    type="button"
                    variant="outline"
                  >
                    <Send />
                    Reenviar e-mail
                  </Button>
                </span>
              </Tooltip>
              <Button
                onClick={() => setDetailRequest(null)}
                type="button"
                variant="outline"
              >
                Fechar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </div>
  );
}
